import { faCompress, faExpand } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GoogleMap, GoogleMapProps } from "@react-google-maps/api"
import { isArray } from "lodash"
import React, { FC, MutableRefObject, PropsWithChildren } from "react"
import { renderToStaticMarkup } from "react-dom/server"
import { ClientInstance } from "../../Client/ClientInstance"
import { GetOrder } from "../../CustomerPortal/CustomerPortalOrders/CustomerPortalOrders"
import { GetProject } from "../../CustomerPortal/CustomerPortalProjectsManager/CustomerPortalProjectsManager"
import { objectOfError } from "../../ErrorBoundary/objectOfError"
import { getLogger } from "../../Logging/getLogger"
import { addModalOpenClass, removeModalOpenClass } from "../../Orders/Components/ModulePopup/ModulePopup"
import { Project } from "../../Orders/order-data-model"
import { AnonymousOrder } from "../../Orders/OrderCompleted/AnonymousOrderResponse"
import {
	markerDefaultOptions,
	PolygonTransportZone,
	transportZoneMarkerDefaultHtml,
} from "../../Orders/ProjectInputModule/ProjectInputModule"
import { anyToLoggableString } from "../anyToLoggableString"
import style from "./GoogleMapComponent.module.css"

const loggerExtFunctions = getLogger("GoogleMapComponent")

declare global {
	interface Document {
		mozCancelFullScreen?: () => Promise<void>
		msExitFullscreen?: () => Promise<void>
		webkitExitFullscreen?: () => Promise<void>
		mozFullScreenElement?: Element
		msFullscreenElement?: Element
		webkitFullscreenElement?: Element
		onwebkitfullscreenchange?: any
		onmsfullscreenchange?: any
		onmozfullscreenchange?: any
	}

	interface HTMLElement {
		msRequestFullScreen?: () => Promise<void>
		mozRequestFullScreen?: () => Promise<void>
		webkitRequestFullScreen?: () => Promise<void>
	}
}

export function addAddressAndExactDeliveryMarkersToMap(
	map: google.maps.Map,
	data: AnonymousOrder | GetOrder | Project | GetProject,
): google.maps.marker.AdvancedMarkerElement[] {
	const ret: google.maps.marker.AdvancedMarkerElement[] = []

	let coordinates
	if ("address" in data) {
		// AnonymousOrder | GetOrder | GetProject | InterimOrder
		coordinates = data.address?.coordinates
	} else {
		// Project (localstorage)
		coordinates = data.coordinates
	}

	let deliveryCoordinates: google.maps.LatLngLiteral | null = null

	if ("execution" in data) {
		// AnonymousOrder | GetOrder | InterimOrder
		deliveryCoordinates = data.execution?.deliveryCoordinates || null
	} else if ("deliveryCoordinates" in data) {
		// Project (localstorage)
		deliveryCoordinates = data.deliveryCoordinates || null
	} // else, nothing, GetProject (from backend) has no delivery coordinates

	if (coordinates) {
		try {
			const marker = new google.maps.marker.AdvancedMarkerElement(markerDefaultOptions("Vald adress"))
			marker.map = map
			marker.position = coordinates
			ret.push(marker)
		} catch (e) {
			loggerExtFunctions.error("Failed to set address marker 1 on map", e)
		}
	}

	if (deliveryCoordinates) {
		try {
			const marker2 = new google.maps.marker.AdvancedMarkerElement(markerDefaultOptions("Önskad leveransplats", true))
			marker2.map = map
			marker2.position = deliveryCoordinates
			ret.push(marker2)
		} catch (e) {
			loggerExtFunctions.error("Failed to set address marker 2 on map", e)
		}
	}
	return ret
}

export function maybeSetMapBoundsForExactDelivery(
	map: google.maps.Map,
	data: AnonymousOrder | GetOrder | Project,
	padding?: google.maps.Padding,
) {
	if (!map) {
		return
	}

	const coordinates = "address" in data ? data.address?.coordinates : data.coordinates
	const deliveryCoordinates = "address" in data ? data?.execution?.deliveryCoordinates : data.deliveryCoordinates

	if (!deliveryCoordinates) {
		return
	}

	const bounds = new google.maps.LatLngBounds()

	if (coordinates) {
		bounds.extend(coordinates)
	}

	if (deliveryCoordinates) {
		bounds.extend(deliveryCoordinates)
	}

	map.fitBounds(bounds, padding || { top: 100, left: 60, right: 60, bottom: 30 })
}

// Custom fullscreen control copied and modified from https://developers.google.com/maps/documentation/javascript/examples/control-replacement
// Since mobile devices don't always support regular fullscreen mode, we have to have our own version as fallback
// Otherwise, google just removes the button, which is kinda annoying
export function initGoogleMapsCustomFullscreenControl(map: google.maps.Map) {
	const mapDiv = map.getDiv()
	if (!mapDiv) {
		return
	}
	const elementToSendFullscreen = map.getDiv().firstChild as HTMLElement
	if (!elementToSendFullscreen) {
		return
	}
	const fullscreenControl = document.createElement("button")
	fullscreenControl.innerHTML = renderToStaticMarkup(
		<>
			<FontAwesomeIcon className="expandIcon" color="black" size={"2x"} icon={faExpand} />{" "}
			<FontAwesomeIcon className="compressIcon" color="black" size={"2x"} icon={faCompress} />
		</>,
	)
	fullscreenControl.classList.add("google-maps-custom-fullscreen-button")

	map.controls[google.maps.ControlPosition.RIGHT_TOP]?.push(fullscreenControl)

	fullscreenControl.onclick = function () {
		if (document.fullscreenEnabled) {
			if (isFullscreen(elementToSendFullscreen)) {
				exitFullscreen()
				map.setOptions({ gestureHandling: "cooperative" })
			} else {
				requestFullscreen(elementToSendFullscreen)
				map.setOptions({ gestureHandling: "greedy" })
			}
		} else {
			if (!elementToSendFullscreen.parentElement) {
				return
			}

			const customHiddenElements = document.getElementsByClassName(
				"google-maps-custom-hidden-fullscreen",
			) as HTMLCollectionOf<HTMLElement>
			if (fullscreenControl.classList.contains("is-fullscreen")) {
				map.setOptions({ gestureHandling: "cooperative" })
				fullscreenControl.classList.remove("is-fullscreen")
				elementToSendFullscreen.parentElement.classList.remove("google-map-container-custom-fullscreen")
				removeModalOpenClass()
				for (let customHiddenElement of customHiddenElements) {
					customHiddenElement.style.display = ""
				}
			} else {
				map.setOptions({ gestureHandling: "greedy" })
				fullscreenControl.classList.add("is-fullscreen")
				elementToSendFullscreen.parentElement.classList.add("google-map-container-custom-fullscreen")
				addModalOpenClass()
				for (let customHiddenElement of customHiddenElements) {
					customHiddenElement.style.display = "none"
				}
			}
		}
	}

	document.onwebkitfullscreenchange =
		document.onmsfullscreenchange =
		document.onmozfullscreenchange =
		document.onfullscreenchange =
			function () {
				if (isFullscreen(elementToSendFullscreen)) {
					fullscreenControl.classList.add("is-fullscreen")
				} else {
					fullscreenControl.classList.remove("is-fullscreen")
					map.setOptions({ gestureHandling: "cooperative" })
				}
			}
}

export function addTransportZoneOverlaysToMap(
	map: google.maps.Map | null,
	overLaysRef: MutableRefObject<CustomPolygon[]>,
	markersRef: MutableRefObject<google.maps.marker.AdvancedMarkerElement[]>,
	sortedZones: PolygonTransportZone[],
	client: ClientInstance,
	project: Partial<Project> | GetProject | null,
	addOnMainMapOverlayClickHandler?: ((overlay: CustomPolygon) => void) | null,
) {
	if (client.features.orderUiRenderTransportZones && isArray(sortedZones) && map) {
		overLaysRef.current.forEach((x) => x.setMap(null))
		overLaysRef.current = []
		markersRef.current.forEach((x) => (x.map = null))
		markersRef.current = []
		if (sortedZones.length > 0) {
			const bounds = new google.maps.LatLngBounds()

			if (sortedZones[0]) {
				sortedZones[0].points.forEach((x) => {
					bounds.extend(x)
				})
			}

			if (!project || Object.keys(project).length === 0) {
				map.setCenter(bounds.getCenter())
			}

			sortedZones.forEach((x) => {
				let shape: CustomPolygon
				const pos = x.nameLocation || x.points[0]

				if (pos) {
					addTextToMap(x.name, pos, map, markersRef)
				}

				shape = new google.maps.Polygon({
					paths: x.points,
					id: x.id,
					zIndex: 1000 - x.priority,
					fillColor: x.color,
					strokeColor: x.edgeColor,
					fillOpacity: x.opacity === undefined ? getFillOpacity(x.priority) : x.opacity,
				} as CustomPolygonOptions) as CustomPolygon
				shape.setMap(map)

				if (addOnMainMapOverlayClickHandler) {
					addOnMainMapOverlayClickHandler(shape)
				}

				overLaysRef.current.push(shape)
			})
		}
	}
}

function addTextToMap(
	text: string,
	position: google.maps.LatLng | google.maps.LatLngLiteral,
	map: google.maps.Map,
	markersRef: MutableRefObject<google.maps.marker.AdvancedMarkerElement[]>,
) {
	try {
		const newMarker = new google.maps.marker.AdvancedMarkerElement({
			map: map,
			position: position,
			content: transportZoneMarkerDefaultHtml(text),
		})

		markersRef.current.push(newMarker)
	} catch (err) {
		logger.error(`Failed to show marker with text message on map, message: ${text}`, anyToLoggableString(err))
	}
}

function getFillOpacity(prio: number): number {
	let ret = 0.05 * prio
	if (ret > 0.4) {
		return 0.4
	}

	return ret
}

export function openCoordinatesInGoogleMaps(coordinates: google.maps.LatLngLiteral | null | undefined) {
	if (!coordinates) {
		return
	}

	const encodedCoordinates = encodeURIComponent(`${coordinates.lat},${coordinates.lng}`)
	window.open(`https://www.google.com/maps/search/?api=1&query=${encodedCoordinates}`, "_blank")
}

function isFullscreen(element: HTMLElement) {
	return (
		(document.fullscreenElement ||
			document.webkitFullscreenElement ||
			document.mozFullScreenElement ||
			document.msFullscreenElement) == element
	)
}

function requestFullscreen(element: HTMLElement) {
	if (element.requestFullscreen) {
		element.requestFullscreen()
	} else if (element.webkitRequestFullScreen) {
		element.webkitRequestFullScreen()
	} else if (element.mozRequestFullScreen) {
		element.mozRequestFullScreen()
	} else if (element.msRequestFullScreen) {
		element.msRequestFullScreen()
	}
}

function exitFullscreen() {
	if (document.exitFullscreen) {
		document.exitFullscreen()
	} else if (document.webkitExitFullscreen) {
		document.webkitExitFullscreen()
	} else if (document.mozCancelFullScreen) {
		document.mozCancelFullScreen()
	} else if (document.msExitFullscreen) {
		document.msExitFullscreen()
	}
}

export type CustomPolygon = google.maps.Polygon & { id: string }
export type CustomPolygonOptions = google.maps.PolygonOptions & { id: string }

type GoogleMapErrorBoundaryProps = PropsWithChildren
type GoogleMapErrorBoundaryState = {
	hasError: boolean
	reloadAttempt: number
}

const logger = getLogger("GoogleMapErrorBoundary")

export class GoogleMapErrorBoundary extends React.Component<GoogleMapErrorBoundaryProps, GoogleMapErrorBoundaryState> {
	constructor(props: any) {
		super(props)
		this.state = { hasError: false, reloadAttempt: 0 }
	}

	static getDerivedStateFromError(error: Error) {
		// Update state so the next render will show the fallback UI.
		return { hasError: true }
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		logger.error(`Google maps component threw error`, objectOfError(error), errorInfo)
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className={style.errorWrapper}>
					<div className={style.errorHeader}>
						Hoppsan!
						<br />
						Något gick fel när kartan skulle laddas in.
					</div>
					<p>
						Vi håller på att åtgärda problemet.
						<br />
						Testa att{" "}
						<button
							type="button"
							disabled={this.state.reloadAttempt >= 5}
							onClick={() => {
								this.setState({
									...this.state,
									hasError: false,
									reloadAttempt: this.state.reloadAttempt + 1,
								})
							}}
							style={{ cursor: "pointer" }}>
							ladda om kartan
						</button>{" "}
						eller kom tillbaka senare. Om felet kvarstår, kontakta din leverantör för mer hjälp.
					</p>
				</div>
			)
		}

		return this.props.children
	}
}

type GoogleMapComponentProps = GoogleMapProps

export const GoogleMapComponent: FC<GoogleMapComponentProps> = (props: GoogleMapComponentProps) => {
	return (
		<GoogleMapErrorBoundary>
			<GoogleMap {...props}></GoogleMap>
		</GoogleMapErrorBoundary>
	)
}
